import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Grid from "@insite/mobius/Icons/Grid";
import List from "@insite/mobius/Icons/List";
import Table from "@insite/mobius/Icons/Table";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import NISGridIconActive from "@tCentro/Icons/NISGridIconActive";
import NISGridIconUnActive from "@tCentro/Icons/NISGridIconUnActive";
import NISListIconActive from "@tCentro/Icons/NISLIstIconActive";
import NISListUnActiveIcon from "@tCentro/Icons/NISListUnActiveIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    backgroundColorOnSelect = "backgroundColorOnSelect",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColorOnSelect]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    loaded: !!getProductListDataView(state).value,
    view: state.pages.productList.view,
    siteName: state.context.website.name,
});

const mapDispatchToProps = {
    setView,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface NISProductListViewSelectStyles {
    wrapper?: InjectableCss;
    listViewClickableUnSelected?: ClickablePresentationProps;
    listViewIconUnSelected?: IconPresentationProps;
    listViewIconSelected?: IconPresentationProps;
    gridViewClickableUnSelected?: ClickablePresentationProps;
    gridViewClickableSelected?: ClickablePresentationProps;
    gridViewIconUnSelected?: IconPresentationProps;
    gridViewIconSelected?: IconPresentationProps;
    tableViewClickable?: ClickablePresentationProps;
    tableViewIconUnSelected?: IconPresentationProps;
    tableViewIconSelected?: IconPresentationProps;
}

export const viewSelectStyles: NISProductListViewSelectStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding-top: 0px; /* Different padding for Nisco */
            float: right; /* Different float for Nisco */
            flex-direction: row-reverse;
            gap: 16px;
            width: 100%;
            @media (min-width: 768px) and (max-width: 991px) {
                float: none;
            }
            > button {
                border-radius: 4px !important; /* Different border-radius for Nisco */
                width: 48px !important; /* Different size for Nisco */
                height: 48px !important;
                padding: 7px !important;
                border: 2px solid #18315e;
                justify-content: center;
            }
        `,
    },
    listViewClickableUnSelected: {
        css: css`
            background: #f0f0f0; /* Different background for Nisco */
            border: 1px solid #e0dfe0 !important;
            justify-content: center;
        `,
    },

    gridViewClickableUnSelected: {
        css: css`
            background: #f0f0f0; /* Different background for Nisco */
            border: 1px solid #e0dfe0 !important;
            justify-content: center;
        `,
    },

    listViewIconUnSelected: {
        src: NISListUnActiveIcon,
        size: 21 /* Different size for Nisco */,
        css: css`
            justify-content: center;
            svg {
                height: 21px;
                width: 21px;
                color: #666; /* Different color for Nisco */
            }
        `,
    },
    listViewIconSelected: {
        src: NISListIconActive,
        size: 21,
        css: css`
            justify-content: center;
            svg {
                height: 21px;
                width: 21px;
                color: #fff; /* Different color for Nisco */
            }
        `,
    },
    gridViewIconUnSelected: {
        src: NISGridIconUnActive,
        size: 21,
        css: css`
            justify-content: center;
            svg {
                height: 21px;
                width: 21px;
                color: #666;
            }
        `,
    },
    gridViewIconSelected: {
        src: NISGridIconActive,
        size: 21,
        css: css`
            justify-content: center;
            svg {
                height: 21px;
                width: 21px;
                color: #fff;
            }
        `,
    },
    tableViewIconUnSelected: {
        src: Table,
        size: 21,
        css: css`
            svg {
                height: 21px;
                width: 21px;
                color: #666;
            }
        `,
    },
    tableViewIconSelected: {
        src: Table,
        size: 21,
        css: css`
            svg {
                height: 21px;
                width: 21px;
                color: #fff;
            }
        `,
    },
};

const styles = viewSelectStyles;

class NISProductListViewSelect extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const view = getCookie("ProductList-View");
        if (view && this.props.view !== view) {
            this.props.setView({ view: view as ProductListViewType });
        }
    }

    render() {
        const {
            loaded,
            view,
            setView,
            fields: { backgroundColorOnSelect },
            siteName,
        } = this.props;

        const listViewClickableSelected = {
            css: css`
                background: ${backgroundColorOnSelect};
            `,
        };

        const gridViewClickableSelected = {
            css: css`
                background: ${backgroundColorOnSelect};
            `,
        };
        if (!loaded) {
            return null;
        }

        const clickListViewHandler = () => {
            changeView("List");
        };

        const clickGridViewHandler = () => {
            changeView("Grid");
        };

        const clickTableViewHandler = () => {
            changeView("Table");
        };

        const changeView = (view: ProductListViewType) => {
            setCookie("ProductList-View", view);
            setView({ view });
        };

        return (
            <StyledWrapper {...styles.wrapper}>
                <Clickable
                    data-test-selector="viewSelectList"
                    {...(view === "List"
                        ? { ...listViewClickableSelected }
                        : { ...styles.listViewClickableUnSelected })}
                    onClick={clickListViewHandler}
                >
                    <VisuallyHidden>{translate("Product List View")}</VisuallyHidden>
                    <Icon
                        {...(view === "List"
                            ? { ...styles.listViewIconSelected }
                            : { ...styles.listViewIconUnSelected })}
                    />
                </Clickable>
                <Clickable
                    data-test-selector="viewSelectGrid"
                    {...(view === "Grid"
                        ? { ...gridViewClickableSelected }
                        : { ...styles.gridViewClickableUnSelected })}
                    onClick={clickGridViewHandler}
                >
                    <VisuallyHidden>{translate("Product Grid View")}</VisuallyHidden>
                    <Icon
                        {...(view === "Grid"
                            ? { ...styles.gridViewIconSelected }
                            : { ...styles.gridViewIconUnSelected })}
                    />
                </Clickable>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NISProductListViewSelect),
    definition: {
        group: "Product List",
        displayName: "NIS View Select",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.backgroundColorOnSelect,
                editorTemplate: "TextField",
                defaultValue: `#8F0446`,
                fieldType: "General",
                displayName: "Background Color",
            },
        ],
    },
};

export default widgetModule;
